import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.15); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

export const Container = styled.div`
  width: 100%;
  height: calc(100vh - 4em);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  position: relative;
  padding: 2em;
  box-sizing: border-box;
  margin-top: 60px;
  transition: all 0.4s ease;

  &.dragging {
    .drop-box {
      border-color: rgba(16, 185, 129, 0.5);
      background-color: rgba(16, 185, 129, 0.04);
      box-shadow: 0 0 40px rgba(16, 185, 129, 0.08);
    }
    
    .drop-area {
      border-color: rgba(16, 185, 129, 0.5) !important;
      background-color: rgba(16, 185, 129, 0.04) !important;
    }
  }

  .upload-content {
    display: flex;
    gap: 4em;
    align-items: center;
    max-width: 68em;
    width: 100%;
    justify-content: center;
    animation: ${fadeInUp} 0.6s ease-out;

    @media (max-width: 900px) {
      flex-direction: column;
      gap: 2em;
    }
  }

  .drop-area-section {
    flex: 1;
    max-width: 520px;
    min-width: 380px;
    display: flex;
    align-items: stretch;
    justify-content: center;

    @media (max-width: 900px) {
      max-width: 420px;
      min-width: 300px;
    }
  }

  .message-section {
    flex: 1;
    max-width: 380px;
    display: flex;
    align-items: center;

    .message {
      h2 {
        font-size: 2.2em;
        font-weight: 700;
        color: ${({ theme }) => theme.text || 'rgba(255, 255, 255, 0.9)'};
        margin-bottom: 0.4em;
        line-height: 1.15;
        letter-spacing: -0.02em;
        animation: ${fadeInUp} 0.6s ease-out;
      }

      .logo {
        margin-bottom: 1.8em;
        animation: ${fadeInUp} 0.5s ease-out;

        img {
          height: 2.8em;
          width: auto;
          opacity: 0.9;
        }
      }

      p {
        font-size: 1em;
        color: ${({ theme }) => theme.text || 'rgba(255, 255, 255, 0.7)'};
        line-height: 1.7;
        margin-bottom: 1.5em;
        opacity: 0.5;
        animation: ${fadeInUp} 0.6s ease-out 0.1s both;
      }

      .features-list {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          background: ${({ theme }) => theme.card || 'rgba(255, 255, 255, 0.03)'};
          border: 1px solid ${({ theme }) => theme.border || 'rgba(255, 255, 255, 0.06)'};
          opacity: 0;
          animation: ${fadeInUp} 0.5s ease-out forwards;
          transition: all 0.25s ease;

          &:hover {
            background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16, 185, 129, 0.04)' : 'rgba(16, 185, 129, 0.06)'};
            border-color: rgba(16, 185, 129, 0.15);
            transform: translateX(4px);
          }

          .feature-icon {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
            flex-shrink: 0;
          }

          span {
            font-size: 0.85em;
            color: ${({ theme }) => theme.text || 'rgba(255, 255, 255, 0.6)'};
            opacity: 0.7;
            font-weight: 500;
          }
        }
      }
    }

    @media (max-width: 900px) {
      max-width: 100%;
      
      .message {
        text-align: center;

        h2 { font-size: 1.6em; }
        p { font-size: 0.95em; }
        
        .features-list {
          display: none;
        }
      }
    }
  }

  .back-button {
    position: absolute;
    top: 16px;
    right: 2em;
    padding: 8px 16px;
    background: transparent;
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.25s ease;
    z-index: 10;

    &:hover {
      background: rgba(16, 185, 129, 0.1);
      border-color: rgba(16, 185, 129, 0.5);
    }
  }
`;

export const DropArea = styled.div`
  width: 100%;
  min-height: 100%;
  height: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text || 'white'};
  transition: all 0.4s ease;
  cursor: ${(props) => (props.$isProcessing ? "wait" : "pointer")};
  animation: ${fadeInUp} 0.6s ease-out 0.15s both;

  .drop-box {
    width: 100%;
    height: 100%;
    display: flex;
    background: ${({ theme }) => theme.card || 'rgba(0, 0, 0, 0.2)'};
    border: 1px solid ${({ theme }) => theme.border || 'rgba(255, 255, 255, 0.08)'};
    border-radius: 20px;
    justify-content: center;
    align-items: center;
    min-height: 420px;
    padding: 1.2em;
    box-sizing: border-box;
    transition: all 0.4s ease;

    .drop-area {
      width: 100%;
      height: 100%;
      min-height: 380px;
      border: 2px dashed
        ${(props) =>
          props.$hasWarning
            ? "rgba(239, 68, 68, 0.4)"
            : props.$isDragging
            ? "rgba(16, 185, 129, 0.5)"
            : props.$isSuccess
            ? "rgba(16, 185, 129, 0.3)"
            : props.theme?.border || 'rgba(255, 255, 255, 0.06)'};
      background-color: ${(props) =>
        props.$hasWarning
          ? "rgba(239, 68, 68, 0.02)"
          : props.$isDragging
          ? "rgba(16, 185, 129, 0.04)"
          : props.$isSuccess
          ? "rgba(16, 185, 129, 0.03)"
          : "transparent"};
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.8em;
      padding: 2em;
      transition: all 0.35s ease;
      box-sizing: border-box;

      &:hover {
        ${(props) =>
          !props.$isProcessing &&
          !props.$hasWarning &&
          `
          border-color: rgba(16, 185, 129, 0.35);
          background-color: rgba(16, 185, 129, 0.03);

          .drop-icon-ring .drop-icon-inner {
            transform: scale(1.08);
            background: rgba(16, 185, 129, 0.15);
          }

          .title { opacity: 1; }
          .subtitle { opacity: 0.7; }
        `}
      }

      /* Animated icon ring */
      .drop-icon-ring {
        position: relative;
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: ${floatSlow} 4s ease-in-out infinite;

        .drop-icon-inner {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2em;
          color: #10b981;
          background: rgba(16, 185, 129, 0.08);
          border: 1px solid rgba(16, 185, 129, 0.15);
          transition: all 0.35s ease;
          z-index: 1;
        }

        .ring-pulse {
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 2px solid rgba(16, 185, 129, 0.25);
          animation: ${pulseRing} 2.5s ease-out infinite;
        }
      }

      .title {
        font-size: 1.15em;
        opacity: 0.85;
        color: ${({ theme }) => theme.text || '#e0e0e0'};
        font-weight: 600;
        margin: 0;
        transition: opacity 0.3s ease;
      }

      .subtitle {
        font-size: 0.85em;
        opacity: 0.4;
        color: ${({ theme }) => theme.text || '#c0c0c0'};
        margin: 0;
        text-align: center;
        transition: opacity 0.3s ease;
      }

      .warning {
        color: #ef4444;
        font-size: 0.85em;
        margin-top: 0.5em;
        opacity: 0.9;
        font-weight: 500;
      }

      .success {
        color: #10b981;
        font-size: 0.85em;
        margin-top: 0.5em;
        opacity: 0.9;
        font-weight: 500;
      }

      .file-types {
        display: flex;
        gap: 6px;
        margin-top: 0.8em;
        flex-wrap: wrap;
        justify-content: center;

        .file-badge {
          padding: 5px 12px;
          background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16, 185, 129, 0.06)' : 'rgba(16, 185, 129, 0.08)'};
          border-radius: 8px;
          font-size: 0.72em;
          color: #10b981;
          font-weight: 600;
          border: 1px solid rgba(16, 185, 129, 0.12);
          letter-spacing: 0.02em;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(16, 185, 129, 0.12);
            border-color: rgba(16, 185, 129, 0.25);
          }
        }
      }

      .progress-container {
        width: 80%;
        margin-top: 1.2em;

        .progress-bar {
          height: 4px;
          background: ${({ theme }) => theme.border || 'rgba(255, 255, 255, 0.06)'};
          border-radius: 2px;
          overflow: hidden;

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #10b981, #34d399);
            border-radius: 2px;
            transition: width 0.3s ease;
          }
        }

        .progress-text {
          font-size: 0.8em;
          color: ${({ theme }) => theme.text || '#fff'};
          opacity: 0.5;
          margin-top: 8px;
          text-align: center;
        }
      }
    }
  }
`;
